import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { AppDispatch } from '../store';
import { fetchApiPortalData } from '../store/apiDefinitions/apiSlice';
import { apiDefinitionsSelector, apiLoadingSelector } from '../store/apiDefinitions/selectors';
import ApiFilterSidebar from '../components/ApiFilterSidebar/ApiFilterSidebar';
import ApiPortalContent from '../components/ApiPortalContent/ApiPortalContent';
import { LoadingSpinner } from '../components/common';
import { Language } from '@/types';
import { getLocalizedContent } from '../utils/apiUtils';
import { useTranslation } from 'react-i18next';
import { ApiDefinition, ApiTag } from '@/types/api-definitions';

const ApiPortal: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { i18n } = useTranslation();
  const apis = useSelector(apiDefinitionsSelector);
  const loading = useSelector(apiLoadingSelector);
  const selectedLanguage = i18n.language as Language;
  const [filteredApiDefinitions, setFilteredApiDefinitions] = React.useState<ApiDefinition[]>([]);

  useEffect(() => {
    dispatch(fetchApiPortalData());
  }, [dispatch]);

  const apisWithAuthoredContent = useMemo(() => {
    return apis.filter((api) => {
      const authoredContent = getLocalizedContent(api, selectedLanguage);
      return !!authoredContent;
    });
  }, [apis, selectedLanguage]);

  useEffect(() => {
    setFilteredApiDefinitions(apisWithAuthoredContent);
  }, [apisWithAuthoredContent]);

  const handleFilterChange = (filters: {
    search: string;
    companies: string[];
    categories: string[];
    apiAccess: string[];
  }) => {
    const { search, companies, categories, apiAccess } = filters;

    let filtered = apisWithAuthoredContent;

    if (search) {
      filtered = filtered.filter((api) => {
        const authoredContent = getLocalizedContent(api, selectedLanguage);
        const searchLower = search.toLowerCase();

        return (
          authoredContent?.title?.toLowerCase().includes(searchLower) ||
          authoredContent?.descriptionShort?.toLowerCase().includes(searchLower) ||
          authoredContent?.descriptionLong?.toLowerCase().includes(searchLower)
        );
      });
    }

    if (companies.length > 0) {
      filtered = filtered.filter((api) => companies.includes(api.company.name));
    }

    if (categories.length > 0) {
      filtered = filtered.filter((api) => {
        const authoredContent = getLocalizedContent(api, selectedLanguage);
        return authoredContent?.tags?.some((tag: ApiTag) => categories.includes(tag.slug));
      });
    }

    if (apiAccess.length === 1) {
      if (apiAccess[0] === 'public') {
        filtered = filtered.filter((api) => api.isPublic);
      } else {
        filtered = filtered.filter((api) => !api.isPublic);
      }
    }

    setFilteredApiDefinitions(filtered);
  };

  if (loading) {
    return <LoadingSpinner height='100vh' />;
  }

  return (
    <Box sx={{ display: 'flex' , p: 3 }}>
      <ApiFilterSidebar onFiltersChange={handleFilterChange} />
      <ApiPortalContent apiDefinitions={filteredApiDefinitions} />
    </Box>
  );
};

export default ApiPortal;
